import { ThemeProps, StyledConfig } from 'styled-components'
import merge from 'lodash/merge'
import shouldForwardProp from '@styled-system/should-forward-prop'
import { spacingConfig } from '../../properties/space'
import { ConfigType, StyleType, ConfigKeys, PropsType } from './types'

export const filterConfig: StyledConfig = {
    shouldForwardProp: (prop: string): boolean =>
        shouldForwardProp(prop) &&
        ![
            ...Object.keys(spacingConfig) /* additional configs which need to be filtered */,
        ].includes(prop),
}

const createFinalPropertyStyle = (
    value: string | number | undefined,
    configProperty: string[] | undefined,
    // eslint-disable-next-line
    scale?: any
): StyleType => {
    const propertyStyle: StyleType = {}
    let finalValue: string | number | undefined

    // If there is a scale defined in the theme and in the config we are getting the correct mapped value
    if (scale && value !== undefined) {
        finalValue = scale[value]
    }
    if (finalValue === undefined) finalValue = value

    // Since the prop name does not always match with the corresponding React Native Steelsheet prop name we need to use the config to map it correctly
    configProperty?.forEach(property => {
        if (finalValue !== undefined) {
            propertyStyle[property] = finalValue
        }
    })

    return propertyStyle
}

/* parameters:
    config: A config in which the following is defined:
                - the mapping from custom prop names to React-Native Stylesheet prop names
                - the mapping from the scale name to its theme value (scale is defined in the theme which will be provided by the client)
    props: The props which the component gets passed through by the client

    return value:
        key value pair and optional media queries based on the breakpoints (e.g. { margin: 30, @media screen and (min-width: 500px): margin: 40 })
 */
// ignore next line because Theme could be anything
// eslint-disable-next-line
const transformProps = (config: ConfigType<ConfigKeys>, props: PropsType & ThemeProps<any>): StyleType => {
    const { breakpoints } = props.theme
    const style: StyleType = {}
    let propertyStyle = {}
    Object.keys(config).forEach(key => {
        const configKey = key as ConfigKeys
        const configProperty: string[] | undefined = config[configKey]?.property
        const configScale = config[configKey]?.scale
        // We only care about the props which are listed in the config
        const value = props[configKey]
        // eslint-disable-next-line
        let scale: any
        if (configScale) {
            scale = props.theme[configScale]
        }
        if (Array.isArray(value)) {
            if (!breakpoints && value.length !== 0) {
                propertyStyle = createFinalPropertyStyle(value[0], configProperty, scale)
            } else {
                value.forEach((property: string | number | undefined, index: number) => {
                    if (index < breakpoints.length && property !== undefined) {
                        const finalPropertyStyle = createFinalPropertyStyle(
                            property,
                            configProperty,
                            scale
                        )
                        if (index === 0) {
                            merge(propertyStyle, finalPropertyStyle)
                        } else {
                            merge(propertyStyle, {
                                [`@media screen and (min-width: ${breakpoints[index]})`]:
                                    finalPropertyStyle,
                            })
                        }
                    }
                })
            }
        } else {
            propertyStyle = createFinalPropertyStyle(value, configProperty, scale)
        }
        merge(style, propertyStyle)
    })

    return style
}

export default transformProps
